import { logger } from '../../logger/Logger';
import { ViewingService, pathToURL } from '../net/Xhr';
import { ErrorCodes } from '../net/ErrorCodes';
import * as et from '../../application/EventTypes';
import { initLoadContext } from "../net/endpoints";
import { ObjectTree } from '../../wgs/scene/ObjectTree';
import { InstanceTreeAccess } from '../../wgs/scene/InstanceTreeStorage';
import { createWorker } from './WorkerCreator';
import { PROPERTY_ACTIONS } from "./WorkerActions";
import { EventDispatcher } from "../../application/EventDispatcher";
import { ReadonlyFilterTableImpl } from "../workers/filter/ReadonlyFilterTableImpl.ts";
import { propHash64Enabled, skipSparseAttributeDefinitions } from "../../wgs/globals";
import { isMobileDevice } from "../../compat";
import { FeatureFlags, InternalFeatureFlags } from "../../application/FeatureFlags";


//Use global property worker threads, which do caching of
//shared property databases (and database files).
const NUM_PROP_WORKERS = isMobileDevice() ? 2 : 6;
const propWorkers = new Array(NUM_PROP_WORKERS);

//Keep track of all pending operations/callbacks going into the property worker
let PROPDB_CB_COUNTER = 1;
const PROPDB_CALLBACKS = {};

/**
 * @param e
 * @private
 */
function propertyWorkerCallback(e) {

  const data = e.data;

  if (data && data.debug) {
    logger.debug(data.message);
    return;
  }

  // Analytics events forwarded from the property worker's OPFSCache (e.g. quota exceeded,
  // cache-open failure). These carry no cbId, so handle them before the callback lookup,
  // mirroring how the geometry cache does it in OtgResourceCache._handleWorkerMessage.
  if (data && data.event && data.properties) {
    Autodesk.Viewing.Private.analytics?.track(data.event, data.properties);
    return;
  }

  // find worker callbacks for this message: [onSuccess, onError, onProgress]
  const cbs = data && data.cbId && PROPDB_CALLBACKS[data.cbId];
  if (!cbs) {
    // Callback for this cbId have been unregistered meanwhile.
    // This may happen if a PropDbLoader.dtor is called before all messages were processed.
    // In this case, the message is outdated and we can ignore it.
    return;
  }

  // Handle progress callbacks. Unlike success/error event, they may be triggered multiple times for the same operation/cbId.
  if (data.progress) {
    if (cbs[2]) {
      cbs[2](data.progress);
    }

    // Note that we don't remove the cbId here yet. The cbId of this operation
    // is cleaned up later when getting the success or error message (see below)
    return;
  }

  if (data.error) {
    if (cbs[1])
    cbs[1](data.error);
  } else {
    if (cbs[0])
    cbs[0](data.result);
  }

  // Getting success/error message implies that the current operation/cbId is finished.
  delete PROPDB_CALLBACKS[data.cbId];
}

/**
 * @param onSuccess
 * @param onError
 * @param onProgress
 * @private
 */
function registerWorkerCallback(onSuccess, onError, onProgress) {
  const cbId = PROPDB_CB_COUNTER++;

  PROPDB_CALLBACKS[cbId] = [onSuccess, onError, onProgress];

  return cbId;
}

/**
 * @param cbId
 * @private
 */
function unregisterWorkerCallback(cbId) {
  delete PROPDB_CALLBACKS[cbId];
}

function hashString(str) {

  if (!str) {
    return 0;
  }

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

/**
 * return the propWorker instance for the given index, or create it if it doesn't exist
 * @param {number} [index=0]
 * @private
 */
function getOrCreatePropWorker(index = 0) {
  if (!propWorkers[index]) {
    propWorkers[index] = createWorker('PropWorker', true);
    propWorkers[index].addEventListener('message', propertyWorkerCallback);
  }
  return propWorkers[index];
}

/**
 * @param opArgs
 * @param onSuccess
 * @param onError
 * @param onProgress
 * @param buffers
 * @private
 */
function asyncPropWorkerOperation(opArgs, onSuccess, onError, onProgress, buffers, workerIndex = 0) {
  opArgs.cbId = registerWorkerCallback(onSuccess, onError, onProgress);
  getOrCreatePropWorker(workerIndex).doOperation(opArgs, buffers);
}

/**
 * Used by Autodesk.Viewing.shutdown() to shutdown the propdb worker threads
 * @private
 */
export function shutdownPropWorker() {
  for (let i = 0; i < NUM_PROP_WORKERS; i++) {
    if (propWorkers[i]) {
      propWorkers[i].terminate();
      propWorkers[i] = null;
    }
  }
}

/**
 * @private
 */
export function clearPropertyWorkerCache() {
  for (let i = 0; i < NUM_PROP_WORKERS; i++) {
    if (propWorkers[i]) {
      propWorkers[i].doOperation({
        "operation": PROPERTY_ACTIONS.UNLOAD_PROPERTYDB,
        "clearCaches": true
      });
    }
  }
}

/**
 * Determines whether the property-DB persistent cache should be used.
 * @returns {boolean}
 * @private
 */
function isPropertyDbCacheEnabled() {
  return FeatureFlags.isEnabled(InternalFeatureFlags.PropertyDbCache);
}

/**
 * Returns the lineage urn for the given model, if available.
 * @param {object} model
 * @returns {string|undefined}
 * @private
 */
function getLineageUrn(model) {
  try {return model?.getDocumentNode()?.lineageUrn?.() || undefined;}
  catch (e) {return undefined;}
}

// State enum to manage delay-loading of optional externalID table.
const IdLoadState = Object.freeze({

  // Indicates that you have to call loadExternalIds() before using externalIds.
  NotLoaded: 0,

  // Indicates that externalIds are available. Either because delay-loading of externalIds is not
  // used or because a prior call to loadExternalIds() succeeded already. Note that this may apply
  // even if propDB is still loading: propDB operations are automatically delayed until propDB is ready.
  Available: 1,

  // Indicates that externalIds were requested via loadExternalIds(), but not finished yet.
  Loading: 2,

  // A prior attempt to load the ids failed.
  Failed: 3
});

// This function is injected into the SVF data and thus created during runtime, but never officially defined or declared. This makes the code hard to work with.
// For backwards compatibility, svf.instanceTree has to be set also.
function makeBackwardsCompatible(propDbLoader) {
  propDbLoader.svf.instanceTree = propDbLoader.instanceTree;
}

/**
 * Per model property database interface, talks to the worker thread behind the scenes.
 *
 * @param sharedDbPath
 * @param model
 * @param eventTarget
 * @class
 * @alias Autodesk.Viewing.Private.PropDbLoader
 */
export class PropDbLoader {

  static useCustomProperties = false;

  static #revitApplicability = null;

  static get revitApplicability() {
    return PropDbLoader.#revitApplicability;
  }

  static set revitApplicability(value) {
    PropDbLoader.#revitApplicability = value ?? null;
  }

  // Need to force refresh the attribute definitions if the 64bit hashing feature flag toggled after initialization
  #cachedHashUse64 = propHash64Enabled();

  // Need to force refresh the attribute definitions if the sparse-attribute filter flag toggled after initialization
  #cachedSkipSparseAttributes = skipSparseAttributeDefinitions();

  #attributesCache = null;

  constructor(sharedDbPath, model, eventTarget) {

    this.eventTarget = eventTarget || new EventDispatcher();

    this.model = model;
    this.svf = model && model.getData();

    //Will be initialized by the complex logic below.
    this.dbPath = "";
    this.sharedDbPath = false;

    //If there is a shared db path and there is no
    //per-SVF specific property database, use the shared one
    //NOTE: The check for .is2d is significant here: In cases where there
    //is an OTG v2 property database, we want to use that. Because OTG does not touch F2D files
    //those might still include the v1 property database in their manifest when we really want to use
    //the v2 OTG property db.
    const isOtg = this.svf && this.svf.loadOptions.bubbleNode && this.svf.loadOptions.bubbleNode.findViewableParent()._getOtgManifest();
    const is2dAndOtg = isOtg && this.svf && this.svf.is2d;
    if (this.svf && !is2dAndOtg && this.svf.propertydb && this.svf.propertydb.avs.length) {

      //If the SVF specified its own property db files, assume they are not shared
      this.dbFiles = this.svf.propertydb;

      for (let f in this.dbFiles) {
        if (this.dbFiles[f][0]) {
          //Revit outputs backslashes in the
          //relative path in the SVF manifest. WTF?
          this.dbFiles[f][0].path = this.dbFiles[f][0].path.replace(/\\/g, "/");
        }
      }

      //Now check if the SVF propertydb definition actually refers to the same property database
      //as the shared database path. This is made harder by various "../../.." relative things
      //in the svf property db files list, hence the nasty path normalization stuff.
      const svfPath = pathToURL(this.svf.basePath);
      if (sharedDbPath) {

        let avsPath = ViewingService.simplifyPath(svfPath + this.svf.propertydb.avs[0].path);
        avsPath = avsPath.slice(0, avsPath.lastIndexOf("/") + 1);

        //Does the property db path specified in the SVF match the
        //one specified as shared db path in the manifest?
        if (avsPath === sharedDbPath) {

          //Convert the property db file list to be relative
          //to the shared property db location, instead of
          //relative to the SVF location

          const dbFilesNew = {};
          for (let f in this.dbFiles) {
            const { path, isShared } = this.dbFiles[f][0];

            let fpath = ViewingService.simplifyPath(svfPath + path);
            if (fpath.indexOf(sharedDbPath) === 0) {
              fpath = fpath.slice(sharedDbPath.length);
            }

            dbFilesNew[f] = [{ path: fpath, isShared }];
          }

          //Replace the loader parameters by the recomputed ones
          this.dbFiles = dbFilesNew;

          //Use the less specific out of the SVF and shared bubble
          //paths, and convert all file paths to be relative from that.
          this.dbPath = sharedDbPath;
          this.sharedDbPath = true;

        } else {
          this.dbPath = svfPath;
          this.sharedDbPath = false;
        }
      } else {
        this.dbPath = svfPath;
        this.sharedDbPath = false;
      }
    } else {
      this.sharedDbPath = true;

      if (this.svf && this.svf.loadOptions.bubbleNode) {
        //NOTE: sharedDbPath is only used here as a cache key in the property worker.
        //Paths returned by the new getPropertyDbManifest API are fully qualified (starting with "urn:"
        this.dbPath = sharedDbPath;
        let pdbManifest = this.svf.loadOptions.bubbleNode.getPropertyDbManifest();
        this.dbFiles = pdbManifest.propertydb;
        this.needsDbIdRemap = pdbManifest.needsDbIdRemap;
      } else {

        //This fallback lets the worker initialize the file list with defaults
        //to preserve behavior if bubbleNode is not given in the model.
        //This code path should be completely removed eventually.
        logger.warn("Deprecated shared property database initialization without bubbleNode in Model load options.");
        this.dbPath = sharedDbPath;
        this.dbFiles = { attrs: [], avs: [], ids: [], values: [], offsets: [] };
      }
      logger.log("Using shared db path " + sharedDbPath);
    }

    // For OTG models, hash on shared_root + pdb_shared_rel_path so that
    // different versions of the same design land on the same worker. This is
    // required for cross-version operations like diffProperties which need
    // both PDBs on the same worker.
    if (isOtg && isOtg.paths && isOtg.pdb_manifest) {
      this.workerIndex = hashString(isOtg.paths.shared_root + isOtg.pdb_manifest.pdb_shared_rel_path) % NUM_PROP_WORKERS;
    } else {
      this.workerIndex = hashString(this.dbPath) % NUM_PROP_WORKERS;
    }

    // Apply needsDbIdRemap override, but only for SVF2
    if (isOtg) {
      this.needsDbIdRemap = this.svf.loadOptions.needsDbIdRemap || this.needsDbIdRemap;
    }

    this.queryParams = "";
    let acmSessionId = this.model?.getDocumentNode()?.getDocument()?.getAcmSessionId();
    acmSessionId = acmSessionId || this.svf?.acmSessionId;
    if (this.svf && acmSessionId) {
      this.queryParams = "acmsession=" + acmSessionId;
    }

    this.loadProgress = 0;

    // Worker callback ID for worker messages during loading.
    this.cbId = undefined;

    // Manage state for optional externalID table.
    this.idLoadState = IdLoadState.NotLoaded;
    this.waitingForExternalIds = []; // Pending {resolve, reject} to be notified when externalId load finished.
  }

  dtor() {
    this.asyncPropertyOperation(
      {
        "operation": PROPERTY_ACTIONS.UNLOAD_PROPERTYDB
      },
      function () {}, function () {}
    );

    // If loading is in progress, make sure that no callbacks are triggered anymore
    const loadStarted = Boolean(this.cbId);
    const loadEnded = this.instanceTree || this.propertyDbError;
    if (loadStarted && !loadEnded) {

      // Disconnect worker callbacks for success, failure, and progress messages
      unregisterWorkerCallback(this.cbId);

      // Some code outside may be waiting for getObjectTree() to fail or succeed.
      // Since we disconnected the worker callbacks, no events will be dispatached anymore.
      // So, we dispatch one right now to avoid getObjectTree() from hanging forever.

      // Note that this.propertyDBError is used by getObjectTree() to distinguish between
      // success and failure. So, we have to set it before triggering the event.
      this.propertyDbError = {
        // Indicates that propDb was unloaded while waiting for getObjectTree()
        propDbWasUnloaded: true
      };

      this.eventTarget.dispatchEvent({
        type: et.OBJECT_TREE_UNAVAILABLE_EVENT,
        svf: this.svf,
        model: this.model,
        target: this
      });
    }

    this.model = null;
    this.svf = null;
    // Drop our reference to the tree (do NOT dispose it — the Model owns it via
    // model.getData().instanceTree and disposes it in Model.#dispose()).
    this.instanceTree = null;
  }

  processLoadResult(result) {
    if (result.instanceTreeStorage) {

      const nodeAccess = new InstanceTreeAccess(result.instanceTreeStorage, result.rootId, result.instanceBoxes);

      this.instanceTree = new ObjectTree(nodeAccess, result.objectCount, result.maxTreeDepth);

      if (this.svf) {
        makeBackwardsCompatible(this);

        // If nodeBoxes are not precomputed, we set the fragBoxes, so that instanceTree can compute nodeBoxes on-the-fly
        this.instanceTree.setFragmentList(this.model.getFragmentList());
      }
    }

    if (result.objectCount) {

      //Case where there is no object tree, but objects
      //do still have properties. This is the case for F2D drawings or when
      //the caller explicitly skipped instanceTree derivation.
      this.hasObjectProperties = result.objectCount;

      if (this.svf) {
        this.svf.hasObjectProperties = result.objectCount;
      }
    }

    if (result.dbidOldToNew) {
      this.model.setDbIdRemap(result.dbidOldToNew);
    }

    this.onLoadProgress(100);
    this.eventTarget.dispatchEvent({
      type: et.OBJECT_TREE_CREATED_EVENT,
      svf: this.svf,
      model: this.model,
      target: this
    });

  }

  processLoadError(error) {

    const scope = this;

    scope.propertyDbError = error;

    scope.onLoadProgress(100);

    scope.eventTarget.dispatchEvent({
      type: et.OBJECT_TREE_UNAVAILABLE_EVENT,
      svf: scope.svf,
      model: scope.model,
      target: scope
    });
  }

  /**
   * Kicks off property database load
   *
   * @param {object} options - Configurations for loading database
   * @param {boolean} options.skipInstanceTreeResult - If set, the loader will skip the step that generates InstanceTreeStorage for use by model tree. Used in server side processing.
   */
  load(options) {
    const scope = this;

    options = PropDbLoader.useCustomProperties ? Object.assign({ useCustomProperties: true }, options) : options || {};

    getOrCreatePropWorker(this.workerIndex);

    const onObjectTreeRead = function (result) {
      scope.processLoadResult(result);
    };

    const onObjectTreeError = function (error) {
      scope.processLoadError(error);
    };

    const onObjectTreeProgress = function (progress) {
      scope.onLoadProgress(progress.percent);
    };

    this.cbId = registerWorkerCallback(onObjectTreeRead, onObjectTreeError, onObjectTreeProgress);

    // Precomputed bboxes are only needed when using the model explode feature. If this is not used, we can save some memory and compute boxes on-the-fly instead.
    const loadOptions = this.svf?.loadOptions;
    const precomputeNodeBoxes = !(loadOptions && loadOptions.disablePrecomputedNodeBoxes);
    const skipExternalIds = !!(loadOptions && loadOptions.skipExternalIds);

    // When not using delay-loading, ids are always available. Note that prop operations do always wait
    // until propDB is loaded. So, we can already assume here that prop operations will get the ids.
    if (!skipExternalIds) {
      this.idLoadState = IdLoadState.Available;
    }

    let cmd;
    if (this.svf && this.svf.instanceTreeInputData && this.svf.instanceBoxes) {
      cmd = PROPERTY_ACTIONS.CREATE_TREE;

    } else {
      cmd = PROPERTY_ACTIONS.LOAD_PROPERTYDB;
    }

    const xfer = {
      operation: cmd,
      dbPath: this.dbPath,
      sharedDbPath: this.sharedDbPath,
      propertydb: this.dbFiles,
      fragToDbId: this.svf?.fragments.fragId2dbId, //the 1:1 mapping of fragment to dbId we got from the SVF or the 1:many we built on the fly for f2d
      fragBoxes: precomputeNodeBoxes && this.svf?.fragments.boxes, //needed to precompute bounding box hierarchy for explode function (and possibly others)
      needsDbIdRemap: this.needsDbIdRemap,
      is2d: this.svf?.is2d,
      cbId: this.cbId,
      queryParams: this.queryParams,
      skipExternalIds: skipExternalIds,
      gltfTree: this.svf.instanceTreeInputData,
      dbToFragId: this.svf?.fragments.dbToFragId,
      fileType: loadOptions?.fileExt,
      usePropertyDbCache: isPropertyDbCacheEnabled(),
      lineageUrn: getLineageUrn(this.model),
      ...options
    };

    let transferable;
    if (this.dbFiles) {
      //In case the property db data is already available (preloaded by a custom loader)
      transferable = [];
      for (const key in this.dbFiles) {
        const data = this.dbFiles[key][0]?.data;
        if (data) {
          transferable.push(data.buffer);
        }
      }
    }
    getOrCreatePropWorker(this.workerIndex).doOperation(initLoadContext(xfer), transferable); // Send data to our worker.

    if (this.svf?.instanceTreeInputData) {
      this.svf.instanceTreeInputData = null;
    }
  }


  asyncPropertyOperation(opArgs, success, fail, progress, buffers) {

    const scope = this;

    //Identify which property database we want to work on (the worker can hold multiple property databases)
    opArgs.dbPath = this.dbPath;

    if (scope.instanceTree || scope.hasObjectProperties) {

      opArgs.cbId = registerWorkerCallback(success, fail, progress);

      getOrCreatePropWorker(this.workerIndex).doOperation(opArgs, buffers); // Send data to our worker.
    } else if (scope.propertyDbError) {
      if (fail) {
        fail(scope.propertyDbError);
      }
    } else {
      const onEvent = function (e) {

        // Since the event is usually emitted by the viewer, we may receive events from
        // other models here too. We have to skip those.
        //
        // Note that this is only sufficient because we can safely assume here that there
        // is always a 1:1 match between models and propDbLoaders.
        //
        // Todo: It would be cleaner to avoid using events from a potentially shared eventTarget, so that
        //       this check wouldn't be needed. But this would require a bit more refactoring
        //       with more risk of potential side effects.
        if (scope.model !== e.model) {
          return;
        }

        scope.eventTarget.removeEventListener(et.OBJECT_TREE_CREATED_EVENT, onEvent);
        scope.eventTarget.removeEventListener(et.OBJECT_TREE_UNAVAILABLE_EVENT, onEvent);
        if (scope.instanceTree || scope.hasObjectProperties || scope.propertyDbError) {
          scope.asyncPropertyOperation(opArgs, success, fail, progress);
        } else
        if (fail) {
          //avoid infinite recursion.
          fail({ code: ErrorCodes.UNKNOWN_FAILURE, msg: "Failed to load properties" });
        }
      };
      scope.eventTarget.addEventListener(et.OBJECT_TREE_CREATED_EVENT, onEvent);
      scope.eventTarget.addEventListener(et.OBJECT_TREE_UNAVAILABLE_EVENT, onEvent);
    }
  }

  /**
   * Gets a list of all available attributes in the property database
   *
   * @param {boolean} [force] - fetch the attribute definitions afresh (and update the cache)
   * @returns {Promise<Array<{ category: string, dataTypeContext: string|undefined, description: string|undefined, flags: number, name: string, precision: number, propertyHash: string }>>}
   * @alias Autodesk.Viewing.Private.PropDbLoader#getAttributeDefinitions
   */
  async getAttributeDefinitions(force) {

    const hash64Enabled = propHash64Enabled();
    const skipSparse = skipSparseAttributeDefinitions();

    // Check if the hashing feature flag is different than the one that was used when caching
    if (!force && this.#attributesCache && hash64Enabled !== this.#cachedHashUse64) {
      force = true;
    }

    // Check if the sparse-attribute filter flag is different than the one that was used when caching
    if (!force && this.#attributesCache && skipSparse !== this.#cachedSkipSparseAttributes) {
      force = true;
    }

    return !force && this.#attributesCache || (await new Promise((resolve, reject) => {
      this.asyncPropertyOperation({ operation: PROPERTY_ACTIONS.GET_ATTR_DEFS, hash64Enabled, skipSparseAttributes: skipSparse }, async (defs) => {
        const ra = PropDbLoader.#revitApplicability;
        if (ra && defs) defs = ra.reconstructConditions(defs);
        this.#cachedHashUse64 = hash64Enabled;
        this.#cachedSkipSparseAttributes = skipSparse;
        this.#attributesCache = defs;
        resolve(defs);
      }, reject);
    }));
  }


  /**
   * Gets the properties for an ID.
   *
   * @deprecated Use getProperties2 instead. This avoids the need to load the externalID table unless explicitly needed.
   * This variant always enforces loading when doing queries with empty filter.
   * @param {number} dbId - The database identifier.
   * @param {Callbacks#onPropertiesSuccess} [onSuccess] - Callback for when the properties are fetched.
   * @param {Callbacks#onGenericError} [onError] - Callback for when the properties are not found or another error occurs.
   * @alias Autodesk.Viewing.Private.PropDbLoader#getProperties
   */
  getProperties(dbId, onSuccess, onError) {

    if (this.idLoadState === IdLoadState.NotLoaded) {
      logger.warn('Calling getProperties() will cause loading of the potentially large externalIDs file. Use getProperties2() to avoid this warning.');
    }
    this.getProperties2(dbId, onSuccess, onError, { needsExternalId: true });
  }

  /**
   * Gets the properties for an ID. New version of getProperties() that avoids loading of externalId table unless really needed.
   *
   * @param {number} dbId - The database identifier.
   * @param {Callbacks#onPropertiesSuccess} [onSuccess] - Callback for when the properties are fetched.
   * @param {Callbacks#onGenericError} [onError] - Callback for when the properties are not found or another error occurs.
   * @param {object} [options]
   * @param {boolean}[options.needsExternalId] - If true, we enforce loading of externalIDs if necessary. ExternalIds may significantly
   * increase memory consumption and should only be loaded if unavoidable.
   * @alias Autodesk.Viewing.Private.PropDbLoader#getProperties2
   */
  getProperties2(dbId, onSuccess, onError, options) {

    const doGetProps = () => {
      this.asyncPropertyOperation(
        {
          "operation": PROPERTY_ACTIONS.GET_PROPERTIES,
          "dbId": dbId,
          "gltfTree": this.svf && this.svf.instanceTreeBackup
        },
        onSuccess, onError
      );
    };

    // Load externalIDs if necessary
    if (options && options.needsExternalId) {
      this.loadExternalIds().
      then(doGetProps).
      catch(onError);
    } else {
      doGetProps();
    }
  }

  /**
   * Bulk property retrieval with property name filter.
   *
   * @deprecated use getBulkProperties2 instead - which makes sure that externalId table is only loaded if really needed.
   *
   * @param {number[]} dbIds - array of object dbIds to return properties for.
   * @param {object} [propFilter] - array of property names to retrieve values for. If empty, all properties are returned.
   * @param {Function} onSuccess - Callback function for when results are ready.
   * @param {Function} onError - Callback function for when something went wrong.
   * @param {boolean} [ignoreHidden=false] - true to ignore hidden properties.
   *
   * @alias Autodesk.Viewing.Private.PropDbLoader#getBulkProperties
   */
  getBulkProperties(dbIds, propFilter, onSuccess, onError, ignoreHidden) {

    const options = {
      ignoreHidden: ignoreHidden,
      propFilter: propFilter,
      needsExternalId: !propFilter || propFilter.includes("externalId") // Include externalID for empty filters or if explicitly requested
    };

    if (options.needsExternalId && this.idLoadState === IdLoadState.NotLoaded) {
      logger.warn('Calling getProperties() will cause loading of the potentially large externalIDs file. Use getProperties2() to avoid this warning.');
    }

    this.getBulkProperties2(dbIds, options, onSuccess, onError);
  }

  /**
   * Bulk property retrieval with property name filter.
   *
   * @param {number[]} dbIds - array of object dbIds to return properties for.
   * @param {object}   [options]
   * @param {string[]} [options.propFilter] - array of property names to retrieve values for. If empty, all properties are returned.
   * @param {string[]} [options.categoryFilter] - array of category names to retrieve values for. If empty, all properties are returned.
   * @param {boolean}  [options.ignoreHidden=false] - true to ignore hidden properties.
   * @param {boolean}  [options.needsExternalId] - If true, it is ensured that externalId table is loaded before doing the property query.
   * @param {Function} onSuccess - Callback function for when results are ready.
   * @param {Function} onError - Callback function for when something went wrong.
   *
   * @alias Autodesk.Viewing.Private.PropDbLoader#getBulkProperties2
   */
  getBulkProperties2(dbIds, options = {}, onSuccess, onError) {

    const doGetProps = () => {
      this.asyncPropertyOperation(
        {
          "operation": PROPERTY_ACTIONS.GET_PROPERTIES,
          "dbIds": dbIds,
          "propFilter": options.propFilter,
          "categoryFilter": options.categoryFilter,
          "ignoreHidden": options.ignoreHidden
        },
        onSuccess, onError
      );
    };

    // Load externalIDs if necessary
    if (options && options.needsExternalId) {
      this.loadExternalIds().
      then(doGetProps).
      catch(onError);
    } else {
      doGetProps();
    }
  }

  /**
   * Retrieves properties related to the specified dbIds.
   * The results object that is passed into the onSuccess callback contains the displayName and displayCategory separated by a '/' as the key and all of the related properties as the entry's value.
   * The results can be used to create a new {@link Autodesk.Viewing.PropertySet|PropertySet} instance.
   * @param {number[]} dbIds - array of object dbIds to return properties for.
   * @param {Object} [options={}]
   * @param {string[]} [options.propFilter] - array of property names to retrieve values for. If empty, all properties are returned.
   * @param {boolean}  [options.ignoreHidden=false] - true to ignore hidden properties.
   * @param {boolean}  [options.needsExternalId] - If true, it is ensured that externalId table is loaded before doing the property query.
   * @param {Function} onSuccess - Callback function for when results are ready.
   * @param {Function} onError - Callback function for when something went wrong.
   * @alias Autodesk.Viewing.Private.PropDbLoader#getPropertySet
   */
  getPropertySet(dbIds, options = {}, onSuccess, onError) {
    const doGetProps = () => {
      this.asyncPropertyOperation(
        {
          operation: PROPERTY_ACTIONS.GET_PROPERTY_SET,
          dbIds: dbIds,
          propFilter: options.propFilter,
          ignoreHidden: options.ignoreHidden,
          fileType: options.fileType
        },
        onSuccess,
        onError
      );
    };

    // Load externalIDs if necessary
    if (options && options.needsExternalId) {
      this.loadExternalIds().then(doGetProps).catch(onError);
    } else {
      doGetProps();
    }
  }


  searchProperties(searchText, attributeNames, onSuccess, onError, options) {

    this.asyncPropertyOperation(
      {
        "operation": PROPERTY_ACTIONS.SEARCH_PROPERTIES,
        "searchText": searchText,
        "attributeNames": attributeNames,
        "searchOptions": options
      },
      onSuccess, onError
    );
  }

  queryProperties(sql, onSuccess, onError, options) {

    this.asyncPropertyOperation(
      {
        "operation": PROPERTY_ACTIONS.QUERY_PROPERTIES,
        sql,
        "queryOptions": options
      },
      onSuccess, onError
    );
  }

  /**
   * Searches the property database for all dbIds that contains a specific property name.
   *
   * @param {string} propertyName - The property name to search for (case sensitive).
   * @returns {Promise<number[]>} The array of dbIds containing the specified property.
   *
   * @private
   */
  findProperty(propertyName) {

    const that = this;
    return new Promise(function (resolve, reject) {
      that.asyncPropertyOperation(
        {
          "operation": PROPERTY_ACTIONS.FIND_PROPERTY,
          "propertyName": propertyName
        },
        resolve, reject
      );
    });
  }

  async getPropertyData({ propertyMapping, valuesProperties, elementsProperties, groupedProperty, dbIds, valuesOnly, composites, predicate, strict, useFilterTable, filterTableOptions }) {

    dbIds ??= Array.from(new Set(this.model.getFragmentList().fragments.fragId2dbId));

    const payload = { propertyMapping, valuesProperties, elementsProperties, groupedProperty, dbIds, valuesOnly, composites, predicate, strict, useFilterTable, filterTableOptions };
    const result = await new Promise((resolve, reject) => this.asyncPropertyOperation({ operation: PROPERTY_ACTIONS.GET_PROPERTY_DATA, payload }, resolve, reject));
    if (result.filterTable) {
      result.filterTable = new ReadonlyFilterTableImpl(result.filterTable);
    }
    return result;
  }

  async getPropertyDataForActions({ propertyMapping, dbIds, actionPredicates }) {
    const payload = { propertyMapping, dbIds, actionPredicates };
    return new Promise((resolve, reject) =>
    this.asyncPropertyOperation({ operation: PROPERTY_ACTIONS.GET_PROPERTY_DATA_FOR_ACTIONS, payload }, resolve, reject)
    );
  }

  async getValuesForAttributes(options = {}) {
    options.operation = PROPERTY_ACTIONS.GET_ATTR_VALUES;
    return new Promise((resolve, reject) => {
      this.asyncPropertyOperation(options, resolve, reject);
    });
  }

  async getFilterPropertyTree(options) {
    options.operation = PROPERTY_ACTIONS.CREATE_FILTER_TREE;
    return await new Promise((resolve, reject) => this.asyncPropertyOperation(options, resolve, reject));
  }

  /**
   * Compares this db with another one
   *
   * The result object passed to `onSuccess` is an object that contains...
   * @example
   *   {
   *      // An array of all dbIds that...
   *      // a) exist in both databases
   *      // b) have different properties.
   *      changedIds: dbId[]
   *
   *      // Optional: details about what changed exactly.
   *      // If k props changed for a dbId result.changeIds[i], result.propChanges[i]
   *      // is an array of length k. Each element in it describes the change of a single
   *      // property.
   *      propChanges: Object[][]
   *   }
   *
   * @param {PropDbLoader} dbToDiff
   * @param {function(number[])} onSuccess - Receives the array of dbIds
   * @param {function(object)} onError - Receives an error if any
   * @param {object} [diffOptions] - Optional diff options
   * @param {number[]} diffOptions.dbIds - Restrict diff to fixed set of dbIds (otherwise all ids)
   * @param {boolean} diffOptions.listPropChanges - If true, result.propChanges is provided (slower)
   * @param {Object} diffOptions.propertyFilter - Ignore properties from diff
   * @param {function(number)} [onProgress] - Optional callback receiving percent values
   *
   * @private
   */
  diffProperties(dbToDiff, onSuccess, onError, diffOptions, onProgress) {
    this.asyncPropertyOperation(
      {
        "operation": PROPERTY_ACTIONS.DIFF_PROPERTIES,
        "dbPath2": dbToDiff, // only pass the second dbPath here. this.dbPath is automatically set (see asyncPropertyOperation)
        "diffOptions": diffOptions
      },
      onSuccess, onError, onProgress
    );
  }

  findLayers(viewScope) {

    const that = this;
    return new Promise(function (resolve, reject) {
      that.asyncPropertyOperation(
        {
          "operation": PROPERTY_ACTIONS.FIND_LAYERS,
          "viewScope": viewScope
        },
        resolve, reject
      );
    });
  }

  /**
   * @param {Function} onSuccess - Callback function for when results are ready.
   * @param {Function} onError - Callback function for when something went wrong.
   * @param {object} [extIdFilter] - optional: restricts result to all extIds for which extIdFilter[extId] is true.
   */
  getExternalIdMapping(onSuccess, onError, extIdFilter) {

    const doQuery = () => {
      this.asyncPropertyOperation(
        {
          "operation": PROPERTY_ACTIONS.BUILD_EXTERNAL_ID_MAPPING,
          "extIdFilter": extIdFilter
        },
        onSuccess, onError
      );
    };
    return this.loadExternalIds().then(doQuery);
  }

  getLayerToNodeIdMapping(onSuccess, onError) {

    this.asyncPropertyOperation(
      {
        "operation": PROPERTY_ACTIONS.BUILD_LAYER_TO_NODE_ID_MAPPING
      },
      onSuccess, onError
    );
  }

  /**
   * Allows executing user supplied function code on the worker thread against the
   * {@link PropertyDatabase} instance. The returned value from the supplied function will
   * be used to resolve the returned Promise.
   *
   * @example
   *     function myCustomFunction(pdb, userData) {
   *          const dbId = 1;
   *          pdb.enumObjectProperties(dbId, function(propId, valueId) {
   *                // do stuff
   *          });
   *          return 42 * userData; // userData will be 2 in this example
   *     }
   *     executeUserFunction(myCustomFunction, 2).then(function(result) {
   *          console.log(result); // result === 84 === 42 * 2
   *     })
   *
   *
   * You can also directly pass in a string, which must contain just the function definition.
   *
   * @example
   *     await executeUserFunction('function myCustomFunction(pdb, userData) { ... }');
   *
   * Note that the function must be fully self-contained, and free of any syntax that causes e.g. babel
   * to change it using polyfills (e.g. decopomposition operators). Since the polyfills don't exist
   * in the worker, the function could fail depending on babeling.
   *
   * @param {Function | string} code - Function takes two arguments, the {@link PropertyDatabase} instance and userData.
   * @param {*} userData - A value that will get passed to the `code` function when run in the property
   *                       worker context. it needs to be serializable.
   *
   * @returns {Promise} - Resolves with the return value of user function.
   *
   * @alias Autodesk.Viewing.Private.PropDbLoader#executeUserFunction
   */
  executeUserFunction(code, userData) {
    if (typeof code === "function") {
      code = code.toString();
    } else if (typeof code !== "string") {
      return Promise.reject("Expected Function or string.");
    }

    // worker code expects the function to be named userFunction
    code = "var userFunction = " + code;

    const blob = new Blob([code], { type: 'application/javascript' });
    const blobUrl = URL.createObjectURL(blob);

    return new Promise((resolve, reject) => {
      this.asyncPropertyOperation(
        {
          "operation": PROPERTY_ACTIONS.USER_FUNCTION,
          "userFunction": blobUrl,
          "userData": userData
        },
        resolve, reject
      );
    });
  }

  isObjectTreeLoaded() {
    return !!this.instanceTree;
  }

  getObjectTree(onSuccess, onError) {
    const scope = this;

    if (scope.instanceTree) {
      onSuccess(scope.instanceTree);
    } else if (scope.propertyDbError) {
      if (onError)
      onError(scope.propertyDbError);
    } else if ('hasObjectProperties' in scope) {
      if (scope.svf && scope.svf.is2d && onError)
      onError('F2D files do not have an InstanceTree.');else

      onSuccess(null); //loaded property database, but instance tree is empty or not initialized
    } else {
      // Property Db has been requested; waiting for worker to complete //
      const listener = function () {
        scope.eventTarget.removeEventListener(et.OBJECT_TREE_CREATED_EVENT, listener);
        scope.eventTarget.removeEventListener(et.OBJECT_TREE_UNAVAILABLE_EVENT, listener);
        scope.getObjectTree(onSuccess, onError);
      };
      scope.eventTarget.addEventListener(et.OBJECT_TREE_CREATED_EVENT, listener);
      scope.eventTarget.addEventListener(et.OBJECT_TREE_UNAVAILABLE_EVENT, listener);
    }
  }

  onLoadProgress(percent) {
    this.eventTarget.dispatchEvent({
      type: et.OBJECT_TREE_LOAD_PROGRESS_EVENT,
      percent,
      svf: this.svf,
      model: this.model,
      target: this
    });
    this.loadProgress = percent;
  }

  /**
   * Estimated load progress in percent.
   *
   * @returns {number} in the range 0..100
   *
   * @alias Autodesk.Viewing.Private.PropDbLoader#getLoadProgress
   */
  getLoadProgress() {return this.loadProgress;}

  /**
   * Returns true if loading is finished (either with success or with error)
   *
   * @returns {boolean}
   *
   * @alias Autodesk.Viewing.Private.PropDbLoader#isLoadDone
   */
  isLoadDone() {return this.loadProgress == 100;}

  // If externalIds were initially skipped using the .skipExternalIds option, this
  // function allows for loading them later on demand.
  //  @returns {Promise} If resolved, we are ready obtain externalIds from the propDB worker.
  //                     It might be rejected if loading failed or propDB was unloaded while waiting.
  loadExternalIds() {

    switch (this.idLoadState) {

      // If ids are already there, we are done.
      case IdLoadState.Available:return Promise.resolve();

      // If we tried to load the ids earlier and failed, we are done as well - just with less success.
      case IdLoadState.Failed:return Promise.reject();

      // If this is the first request for the id-file, send request to worker
      case IdLoadState.NotLoaded:{

          // Avoid to do it again on next call
          this.idLoadState = IdLoadState.Loading;

          // On success, notify everyone that ids are ready
          const onSuccess = () => {
            this.idLoadState = IdLoadState.Available;
            this.waitingForExternalIds.forEach((p) => p.resolve());
          };

          // On failure, notify everyone that it doesn't make sense to wait anymore
          const onError = () => {
            this.idLoadState = IdLoadState.Failed;
            this.waitingForExternalIds.forEach((p) => p.reject());
          };

          const options = {
            operation: PROPERTY_ACTIONS.LOAD_EXTERNAL_IDS,
            // Note that values in this.dbFiles are not strings, but 1-element arrays of those.
            idsFile: this.dbFiles.ids[0],
            queryParams: this.queryParams,
            usePropertyDbCache: isPropertyDbCacheEnabled(),
            lineageUrn: getLineageUrn(this.model)
          };

          // Configure endpoint, credentials etc.
          initLoadContext(options);

          // Setup worker request to load ids file. Note that using asyncPropertyOperation makes sure
          // that this request is delayed until the other propDb files are available.
          this.asyncPropertyOperation(options, onSuccess, onError);
          break;
        }
    }

    // If we get here, the request for id loading must be in progress => resolve/reject later
    return new Promise((resolve, reject) => {
      this.waitingForExternalIds.push({ resolve, reject });
    });
  }

  /**
   * Sends a custom property operation to the worker and ensures the result is
   * attached to the property database. If the PDB hasn't finished loading yet,
   * the operation is re-sent via asyncPropertyOperation (which waits for PDB
   * readiness) with attachToPropertyDb = true.
   *
   * @param {object} options - operation payload (must already have `operation` set).
   * @returns {Promise<object>}
   * @private
   */
  #executeAndAttachToPdb(options) {
    return new Promise((resolve, reject) => {
      const pdbHasLoaded = this.instanceTree || 'hasObjectProperties' in this;
      const onDone = (result) => {
        if (result?.defsChanged) {
          this.#attributesCache = null;
        }
        resolve(result);
      };
      const onSuccess = (result) => {
        if (pdbHasLoaded) {
          onDone(result);
        } else {
          options.attachToPropertyDb = true;
          this.asyncPropertyOperation(options, onDone, reject);
        }
      };
      options.cbId = registerWorkerCallback(onSuccess, reject);
      options.dbPath = this.dbPath;
      getOrCreatePropWorker(this.workerIndex).doOperation(options);
    });
  }

  /**
   * Refreshes the custom properties values for a property database.
   * If called for the first time, attaches the results to the property database.
   *
   * @param {object} options - the configured request for the get custom properties values endpoint.
   * @param {string} options.baseUrl - the base url of the get custom properties values endpoint.
   * @param {string} options.projectId - the project Id for the seed file URN
   * @param {string} options.seedFileUrn - the seed file URN
   * @param {Record<string, string>} options.headers - the headers to send with the request.
   * @private
   */
  refreshCustomPropertiesValues(options) {
    options.operation = PROPERTY_ACTIONS.REFRESH_CUSTOM_PROPERTIES_VALUES;
    return this.#executeAndAttachToPdb(options);
  }

  /**
   * Sets custom properties values.
   *
   * @param {object} options - the configured request for the set custom properties values endpoint.
   * @param {string} options.baseUrl - the base url of the set custom properties values endpoint.
   * @param {string} options.projectId - the project Id for the seed file URN.
   * @param {string} options.seedFileUrn - the seed file URN.
   * @param {number[]} options.selection - the ids of the objects to assign custom properties to.
   * @param {Record<string, any>} options.props - { prop1Key: prop1Value, prop2Key: prop2Value, ... }.
   * @param {Record<string, string>} options.headers - the headers to send with the request.
   * @private
   */
  setCustomPropertiesValues(options) {
    return new Promise((resolve, reject) => {
      options.operation = PROPERTY_ACTIONS.SET_CUSTOM_PROPERTIES_VALUES;
      options.dbPath = this.dbPath;
      this.asyncPropertyOperation(options, resolve, reject);
    });
  }


  /**
   * Adds a chunk to the custom properties batch job.
   *
   * @param {object} options - the configured request for the add custom properties chunk endpoint.
   * @param {string} options.baseUrl - the base url of the add custom properties chunk endpoint.
   * @param {string} options.projectId - the project Id for the seed file URN.
   * @param {string} options.seedFileUrn - the seed file URN.
   * @param {string} options.jobId - the job ID.
   * @param {AssignCustomPropValues[]} options.assignments - the assignments to add to the chunk.
   * @param {Record<string, string>} options.headers - the headers to send with the request.
   * @private
   */
  addCustomPropertiesChunk(options) {
    return new Promise((resolve, reject) => {
      options.operation = PROPERTY_ACTIONS.ADD_CUSTOM_PROPERTIES_CHUNK;
      options.dbPath = this.dbPath;
      this.asyncPropertyOperation(options, resolve, reject);
    });
  }

  /**
   * Gets the custom properties definitions for a project id and lineage URN.
   * @param {object} options - the configured request for the get custom properties definitions endpoint.
   * @param {string} options.baseUrl - the base url of the get custom properties definitions endpoint.
   * @param {string} options.projectId - the project Id for the seed file URN
   * @param {string} options.seedFileUrn - the seed file URN (the back end accepts both lineage and version URNs)
   * @param {Record<string, string>} options.headers - the headers to send with the request.
   * @private
   */
  getCustomPropertiesDefinitions(options) {
    return new Promise((resolve, reject) => {
      options.operation = PROPERTY_ACTIONS.GET_CUSTOM_PROPERTIES_DEFINITIONS;
      options.dbPath = this.dbPath;
      const onResult = (attrs) => {
        const ra = PropDbLoader.#revitApplicability;
        resolve(ra && attrs ? ra.reconstructConditions(attrs) : attrs);
      };
      asyncPropWorkerOperation(options, onResult, reject, undefined, undefined, this.workerIndex);
    });
  }

  /**
   * Pushes pre-fetched custom property field definitions to the worker,
   * bypassing the streaming fetch path.
   *
   * @param {object} options
   * @param {FieldsDef[]} options.fieldsDefs - pre-fetched definitions
   * @returns {Promise<{ defsChanged: boolean, changedKeys: string[] }>}
   * @private
   */
  populateCustomProperties(options) {
    options.operation = PROPERTY_ACTIONS.POPULATE_CUSTOM_PROPERTIES;
    return this.#executeAndAttachToPdb(options);
  }

  static getProvider() {
    return this.#currentProvider ?? this.DEFAULT_PROVIDER;
  }

  static setProvider(provider) {
    this.#currentProvider = provider;
  }

  static DEFAULT_PROVIDER = {
    create: (dbPath, model, viewerApi) => new PropDbLoader(dbPath, model, viewerApi)
  };
  static #currentProvider = this.DEFAULT_PROVIDER;
}